import { Button } from "@chakra-ui/react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { useSelector } from "react-redux";

import { PlusIcon } from "@nens/ranalib";
import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import DurationInput from "../components/DurationInput";
import ErrorMessage from "../components/ErrorMessage";
import NumberInput from "../components/NumberInput";
import { remindUserToPressPlay } from "../state/mainGui";
import { getSimulation, getSimulationStatus } from "../state/simulation";
import { buttonRibbonStyle } from "../styles/styleComposition";
import { mmPerHourToMPerSecond } from "../utils/conversions";
import { durationInputToSeconds } from "../utils/timeConverter";

import { FormLabel } from "./FormLabel";
import styles from "./RainMenu.module.css";

type Errors = JSX.Element[];

const RainMenuCustom: React.FC<ReturnType<typeof mapDispatchToProps>> = ({
  remindUserToPressPlay,
}) => {
  const [intensityMm, setIntensityMm] = useState<number>(20);
  const [durationHoursMinutes, setDurationHoursMinutes] = useState<string>("1:00");
  const durationSeconds = durationInputToSeconds(durationHoursMinutes);
  const [triedToSubmit, setTriedToSubmit] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [communicationError, setCommunicationError] = useState<boolean>(false);
  const simulation = useSelector(getSimulation);
  const simulationStatus = useSelector(getSimulationStatus);

  if (!simulation) return null;

  const getOffset = (): number => {
    // We let the rain start at the current simulation time
    return simulationStatus.time || 0;
  };

  const validate = () => {
    // Return an array of FormattedMessages with errors
    // If array is empty, everything is OK
    const errors: Errors = [];

    if (!simulation || simulation.id === undefined) {
      // if no simulation is existing return
      // probably also dispatch error message to user
      errors.push(
        <FormattedMessage id="rainmenu.error.nosimulation" defaultMessage="No simulation found." />
      );
    }

    if (simulation.duration === undefined) {
      errors.push(
        <FormattedMessage
          id="rainmenu.error.simulationnoduration"
          defaultMessage="Simulation has no duration."
        />
      );
      return errors;
    }

    if (getOffset() + durationSeconds > simulation.duration) {
      errors.push(
        <FormattedMessage
          id="rainmenu.error.durationtoolong"
          defaultMessage="Chosen interval lasts longer than the remaining simulation."
        />
      );
    }

    if (intensityMm <= 0) {
      errors.push(
        <FormattedMessage
          id="rainmenu.error.intensitypositive"
          defaultMessage="Intensity must be positive."
        />
      );
    }

    return errors;
  };

  const addRainEventToSimulation = () => {
    if (!triedToSubmit) {
      // Only show errors after the first time user tried to submit
      setTriedToSubmit(true);
    }

    if (validate().length > 0) return;

    // make request details
    const v3Api = getV3Api();
    const rainCustomSimulationId = simulation.id;
    if (rainCustomSimulationId === undefined) return;
    const simulationsEventsRainConstantCreateRequest = {
      simulationPk: rainCustomSimulationId,
      data: {
        offset: getOffset(),
        duration: durationSeconds,
        value: mmPerHourToMPerSecond(intensityMm),
        units: api.TimeseriesRainMsUnitEnum.M_S,
      },
    };

    setSubmitting(true);

    // send request to the api
    v3Api
      .simulationsEventsRainConstantCreate(simulationsEventsRainConstantCreateRequest)
      .then(() => {
        // Reset form
        setTriedToSubmit(false);
        setCommunicationError(false);
        setSubmitting(false);
      })
      .catch((error) => {
        setCommunicationError(true);
        setSubmitting(false);
        console.error(
          "Rain to api failed with request:",
          simulationsEventsRainConstantCreateRequest,
          "error:",
          error
        );
      });

    remindUserToPressPlay();
  };

  return (
    <div className={styles.CustomRain}>
      <FormLabel>
        <FormattedMessage id="newRainMenu.intensity_label" defaultMessage="Intensity" />
      </FormLabel>
      <NumberInput
        value={intensityMm + ""}
        unit={<FormattedMessage id="newRainMenu.intensity_unit_mm/h" defaultMessage="mm/h" />}
        setValue={(value: string) => setIntensityMm(parseInt(value, 10) || 0)}
        zeroIsInvalid
        required
      />
      <FormLabel>
        <FormattedMessage id="newRainMenu.duration_label" defaultMessage="Duration" />
      </FormLabel>
      <DurationInput
        value={durationHoursMinutes}
        setValue={(value) => {
          setDurationHoursMinutes(value);
        }}
      />
      <div className={buttonRibbonStyle}>
        <Button
          leftIcon={<PlusIcon />}
          variant="secondary"
          onClick={(e) => {
            const target = e.target as HTMLButtonElement;
            if (target.form && target.form.checkValidity && target.form.checkValidity()) {
              addRainEventToSimulation();
            }
          }}
          isDisabled={submitting ? true : undefined}
        >
          <FormattedMessage id="newRainMenu.create_rain_button" defaultMessage="Create" />
        </Button>
      </div>

      <ErrorMessage
        error={communicationError}
        errorMessage={
          <FormattedMessage id="communicationerror" defaultMessage="Failed to send request." />
        }
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
  };
};

export default connect(null, mapDispatchToProps)(RainMenuCustom);
